import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Socket, io } from 'socket.io-client';

interface IOnConnect {
  url: string;
  query?: { [_: string]: any };
  connectCallback?: () => void;
  disconnectCallback?: () => void;
}

interface ISocketContext {
  connected: boolean;
  emit: (event: string, data?: any) => void;
  subscribe: (event: string, handler: (...args: any[]) => void) => void;
  unsubscribe: (event: string, handler: (...args: any[]) => void) => void;
  connect: (data: IOnConnect) => void;
  disconnect: () => void;
}

const SocketContext = createContext({} as ISocketContext);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = (
  props,
) => {
  const { children } = props;

  const socketRef = useRef<Socket | null>(null);
  const connectingRef = useRef(false);
  const [connected, setConnected] = useState(false);

  const emit = useCallback(
    (event: any, data: any) =>
      new Promise<void>((resolve, reject) => {
        if (socketRef.current) {
          console.log(`EMITTING ${event} with data: ${JSON.stringify(data)}`);
          socketRef.current.emit(event, data, (response: any) => {
            console.log(
              `Socket Event Acknowledgement for ${event}: ${JSON.stringify(
                response,
              )}`,
            );
            resolve(response);
          });
        } else {
          reject('No socket yet');
        }
      }),
    [],
  );

  const subscribe = useCallback(
    (event: string, handler: (...args: any[]) => void) => {
      if (socketRef.current) {
        socketRef.current.on(event, handler);
      }
    },
    [],
  );

  const unsubscribe = useCallback(
    (event: string, handler: (...args: any[]) => void) => {
      if (socketRef.current) {
        socketRef.current.removeListener(event, handler);
      }
    },
    [],
  );

  const connect = useCallback(
    ({ url, query, connectCallback, disconnectCallback }: IOnConnect) => {
      if (connectingRef.current || socketRef.current) {
        return;
      }
      connectingRef.current = true;

      const socket = io(url, {
        transports: ['websocket'],
        query,
      });

      socket.on('connect', () => {
        socketRef.current = socket;
        connectingRef.current = false;
        setConnected(true);
        connectCallback && connectCallback();
      });

      socket.on('connect_error', (err) => {
        // eslint-disable-next-line no-console
        console.warn('SocketContext.connect_error', err);
      });

      socket.on('disconnect', () => {
        if (socketRef.current) {
          socketRef.current.removeAllListeners();
        }
        setConnected(false);
        connectingRef.current = false;
        socketRef.current = null;
        disconnectCallback && disconnectCallback();
      });
    },
    [],
  );

  const disconnect = useCallback(({ clearCache = true } = {}) => {
    if (!socketRef.current) {
      return;
    }
    socketRef.current.disconnect();
    setConnected(false);
    if (clearCache) {
      connectingRef.current = false;
      socketRef.current = null;
    }
  }, []);

  return (
    <SocketContext.Provider
      value={{
        connected,
        emit,
        subscribe,
        unsubscribe,
        connect,
        disconnect,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
